import { Badge, Box, Code, HStack, Text } from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/common/DataTable'
import type { ColumnConfig, FilterConfig } from '@/components/common/DataTable/types'
import { useOrderStateEvents } from '@/hooks/useOrderConsistencyData'
import type { AdminOrderStateEvent } from '@/types'
import { formatDateTime } from '@/utils/formatUtils'

type OrderStateEventsPanelProps = {
  isActive: boolean
}

export function OrderStateEventsPanel({ isActive }: OrderStateEventsPanelProps) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [filters, setFilters] = useState<Record<string, string>>({})
  const params = useMemo(() => ({
    page,
    limit: pageSize,
    orderId: filters.orderId || undefined,
    eventType: filters.eventType || undefined,
    source: filters.source || undefined,
    paymentLogId: filters.paymentLogId || undefined,
  }), [filters, page, pageSize])
  const query = useOrderStateEvents(params, { enabled: isActive })
  const data = query.data

  const columns: ColumnConfig<AdminOrderStateEvent>[] = [
    {
      key: 'createdAt',
      title: t('auditLogs.orderStateEvents.columns.createdAt'),
      width: '15%',
      render: (value, row) => (
        <Box>
          <Text fontSize="xs">{formatDateTime(value)}</Text>
          <Code fontSize="10px">{row.id}</Code>
        </Box>
      ),
    },
    {
      key: 'orderId',
      title: t('auditLogs.orderStateEvents.columns.orderId'),
      width: '15%',
      render: value => <Code fontSize="xs">{value}</Code>,
    },
    {
      key: 'eventType',
      title: t('auditLogs.orderStateEvents.columns.eventType'),
      width: '14%',
      render: value => <Badge colorScheme="blue">{value}</Badge>,
    },
    {
      key: 'sequence',
      title: t('auditLogs.orderStateEvents.columns.sequence'),
      width: '8%',
    },
    {
      key: 'source',
      title: t('auditLogs.orderStateEvents.columns.source'),
      width: '12%',
      render: (value, row) => (
        <Box>
          <Text fontSize="xs">{value}</Text>
          {row.paymentLogId && <Code fontSize="10px">{row.paymentLogId}</Code>}
        </Box>
      ),
    },
    {
      key: 'currentStatusId',
      title: t('auditLogs.orderStateEvents.columns.statusTransition'),
      width: '18%',
      render: (value, row) => (
        <HStack spacing={1} fontSize="xs">
          <Text>{row.previousStatusId ?? t('common.na')}</Text>
          <Text aria-hidden="true">→</Text>
          <Text fontWeight="semibold">{value}</Text>
        </HStack>
      ),
    },
    {
      key: 'currentOnchainBalance',
      title: t('auditLogs.orderStateEvents.columns.balanceTransition'),
      width: '18%',
      render: (value, row) => (
        <HStack spacing={1} fontSize="xs">
          <Text>{row.previousOnchainBalance ?? t('common.na')}</Text>
          <Text aria-hidden="true">→</Text>
          <Text fontWeight="semibold">{value ?? t('common.na')}</Text>
        </HStack>
      ),
    },
  ]

  const filterConfig: FilterConfig[] = [
    {
      key: 'orderId',
      type: 'text',
      label: t('auditLogs.orderStateEvents.filters.orderId'),
      placeholder: t('auditLogs.orderStateEvents.filters.orderId'),
      width: '260px',
    },
    {
      key: 'eventType',
      type: 'text',
      label: t('auditLogs.orderStateEvents.filters.eventType'),
      placeholder: t('auditLogs.orderStateEvents.filters.eventType'),
      width: '220px',
    },
    {
      key: 'source',
      type: 'text',
      label: t('auditLogs.orderStateEvents.filters.source'),
      placeholder: t('auditLogs.orderStateEvents.filters.source'),
      width: '220px',
    },
    {
      key: 'paymentLogId',
      type: 'text',
      label: t('auditLogs.orderStateEvents.filters.paymentLogId'),
      placeholder: t('auditLogs.orderStateEvents.filters.paymentLogId'),
      width: '260px',
    },
  ]

  return (
    <Box>
      <DataTable<AdminOrderStateEvent>
        data={data?.events ?? []}
        loading={query.isLoading}
        error={query.error}
        columns={columns}
        filters={filterConfig}
        initialFilters={filters}
        onFiltersChange={values => {
          setFilters(values as Record<string, string>)
          setPage(1)
        }}
        showFilterBar
        pagination={{
          page,
          pageSize,
          total: data?.pagination.total ?? 0,
          pageSizeOptions: [25, 50, 100],
        }}
        onPageChange={setPage}
        onPageSizeChange={size => {
          setPageSize(size)
          setPage(1)
        }}
        size="sm"
        emptyMessage={t('auditLogs.orderStateEvents.empty')}
        showRefresh
        onRefresh={() => query.refetch()}
        refreshing={query.isFetching}
        mobileBreakpoint="md"
      />
      {data?.observedAt && (
        <Text mt={2} fontSize="xs" color="gray.500" textAlign="right">
          {t('monitoring.sideEffectJobs.observedAt', {
            time: formatDateTime(data.observedAt),
          })}
        </Text>
      )}
    </Box>
  )
}
